import { useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Layout } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import type { Bestaetigungsfrage } from "@kundeninfo/ui";
import { Formular, Hinweis, Knopf } from "@kundeninfo/ui/me-design";

/**
 * DIE VARIANTEN EINES LAYOUTS (Spec „Layout-Varianten", 21.10.2026, Oberfläche › Belegdesign; Entscheide 247, 257):
 * fünf Leise-Knöpfe im Slot `aktionen` des Steckbriefs. „Kopie anlegen …" und „Umbenennen …" fragen den Namen im
 * strikten Modal — dort ist Speichern die Bestätigung (Dirk 216c), mit `Formular` + `useFormularZustand` wie die
 * übrigen Modalmasken (Feld gesperrt, solange der Aufruf läuft; 422 am Feld mit Fokussprung, Befunde fremder Felder
 * als Meldung im Modal); „Als Vorgabe", „Archivieren"/„Wiederherstellen"
 * und „Löschen …" fragen gestapelt nach (Hausregel 19.10., Löschen als Gefahr-Frage).
 *
 * Gesperrt, solange der Editor ungespeicherte Eingaben, einen fremden Stand oder einen laufenden Weg hat — eine Kopie
 * nähme sonst den gespeicherten, nicht den sichtbaren Entwurf. Die Regeln (wer Vorgabe werden oder gelöscht werden
 * darf) entscheidet der Server; die Knöpfe spiegeln nur, was die Sicht sagt (`vorgabe`, `archiviertAm`,
 * `verwendung`). Eine 422 kommt trotzdem als Meldung im Kopf an (`onFehler`).
 */

export type LayoutVariantenClient = Pick<ApiClient, "copyLayout" | "renameLayout" | "setDefaultLayout"
  | "archiveLayout" | "deleteLayout">;

export type LayoutVariantenProps = {
  client: LayoutVariantenClient;
  stand: Layout;
  gesperrt: boolean;
  onArbeitet: (laeuft: boolean) => void;
  /** Umbenannt, Vorgabe, Archiv: der neue Stand des GEÖFFNETEN Layouts samt Quittung. */
  onStand: (neu: Layout, meldung: string) => void;
  /** Die Kopie ist angelegt — der Editor wechselt auf sie. */
  onKopie: (neu: Layout) => void;
  /** Gelöscht — `name` des gelöschten Layouts, `ablageHinweis`, wenn der Eintrag der Engine liegen blieb. */
  onGeloescht: (name: string, ablageHinweis: string | null) => void;
  onFehler: (fehler: unknown, ersatz: string) => void;
};

type NamensDialog = { art: "kopie" | "name"; vorschlag: string; name: string };

/** Ergebnis des Namenswegs: der neue Stand — oder ein Fehler, der NICHT ins Modal gehört (409, 422 ohne Feld, Netz):
 *  Der geht wie bisher an `onFehler` (409 → fremder Stand im Editor, sein „Neu laden" bleibt der einzige). */
type NamensErgebnis = { neu: Layout } | { fehler: unknown };

/** Die Kennwert-Zeile „Verwendung" im Steckbrief. */
export function verwendungText(s: Pick<Layout, "vorgabe" | "verwendung" | "archiviertAm">): string {
  const art = s.vorgabe ? "Vorgabe des Unternehmens" : "Variante";
  const belege = s.verwendung === 1 ? "1 Beleg" : `${s.verwendung} Belege`;
  return `${art} · ${belege} mit dieser Wahl${s.archiviertAm === null ? "" : " · archiviert"}`;
}

export function LayoutVarianten(p: LayoutVariantenProps): ReactElement {
  const [dialog, setDialog] = useState<NamensDialog | null>(null);
  const { bestaetigen } = useBestaetigung();
  const s = p.stand;
  const archiviert = s.archiviertAm !== null;
  /** Der Aufruf, den `formular.aktion()` gleich ausführt (Kopie oder Umbenennen). */
  const auftrag = useRef<() => Promise<Layout>>(async () => s);
  const formular = useFormularZustand<NamensErgebnis>({
    speichern: async () => {
      try {
        return { neu: await auftrag.current() };
      } catch (f) {
        // Nur eine 422 MIT Feldern bleibt im Modal (am Feld bzw. als Meldung); alles andere wie bisher an den Editor.
        if (f instanceof ApiError && f.status === 422 && (f.fields ?? []).length > 0) throw f;
        return { fehler: f };
      }
    },
    neuLaden: async () => { /* eine 409 erreicht das Formular nicht (siehe `speichern`) */ },
    bekannteFelder: ["name"],
    beschriftungen: { name: "Name" },
  });

  function oeffnen(art: "kopie" | "name"): void {
    if (p.gesperrt) return;
    // Den Kopienamen schlägt der Server vor (`kopieVorschlag` der Sicht, Domäne `kopieName` samt Zähler „ (2)").
    const vorschlag = art === "kopie" ? s.kopieVorschlag : s.name;
    formular.zuruecksetzen();
    setDialog({ art, vorschlag, name: vorschlag });
  }

  async function namensWeg(d: NamensDialog): Promise<void> {
    if (formular.laeuft) return;
    auftrag.current = d.art === "kopie" ? () => p.client.copyLayout(s.id, d.name)
      : () => p.client.renameLayout(s.id, s.version, d.name);
    p.onArbeitet(true);
    try {
      const ergebnis = await formular.aktion();
      if (ergebnis === undefined) return;
      setDialog(null);
      if ("fehler" in ergebnis) {
        p.onFehler(ergebnis.fehler, d.art === "kopie" ? "Die Kopie konnte nicht angelegt werden."
          : "Das Layout konnte nicht umbenannt werden.");
      } else if (d.art === "kopie") {
        p.onKopie(ergebnis.neu);
      } else {
        p.onStand(ergebnis.neu, `Umbenannt in „${ergebnis.neu.name}“.`);
      }
    } finally {
      p.onArbeitet(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (dialog === null || formular.laeuft) return;
    if (await darfAbbrechen(dialog.name !== dialog.vorschlag)) setDialog(null);
  }

  async function mitFrage(frage: Bestaetigungsfrage, aufruf: () => Promise<void>, ersatz: string):
      Promise<void> {
    if (p.gesperrt || !(await bestaetigen(frage))) return;
    p.onArbeitet(true);
    try {
      await aufruf();
    } catch (f) {
      p.onFehler(f, ersatz);
    } finally {
      p.onArbeitet(false);
    }
  }

  function vorgabe(): void {
    void mitFrage({ titel: "Als Vorgabe festlegen?",
      text: `Belege von ${s.unternehmen.name} ohne eigene Layoutwahl rendern ab jetzt mit „${s.name}“. `
        + "Festgeschriebene Belege behalten ihr Layout.", bestaetigenText: "Als Vorgabe festlegen" },
    async () => {
      const neu = await p.client.setDefaultLayout(s.id, s.version);
      p.onStand(neu, `„${neu.name}“ ist jetzt die Vorgabe.`);
    }, "Die Vorgabe konnte nicht gewechselt werden.");
  }

  function archiv(): void {
    void mitFrage(archiviert
      ? { titel: "Layout wiederherstellen?", text: `„${s.name}“ steht danach wieder zur Wahl am Beleg.`,
          bestaetigenText: "Wiederherstellen" }
      : { titel: "Layout archivieren?", text: `„${s.name}“ steht danach nicht mehr zur Wahl; Belege, die es `
          + "gewählt haben, rendern weiter damit.", bestaetigenText: "Archivieren" },
    async () => {
      const neu = await p.client.archiveLayout(s.id, s.version, !archiviert);
      p.onStand(neu, archiviert ? `„${neu.name}“ ist wiederhergestellt.` : `„${neu.name}“ ist archiviert.`);
    }, archiviert ? "Das Layout konnte nicht wiederhergestellt werden."
      : "Das Layout konnte nicht archiviert werden.");
  }

  function loeschen(): void {
    void mitFrage({ titel: "Layout löschen?", text: `„${s.name}“ wird samt Grafiken gelöscht. Das lässt sich `
      + "nicht rückgängig machen.", bestaetigenText: "Löschen", gefaehrlich: true },
    async () => {
      const ergebnis = await p.client.deleteLayout(s.id, s.version);
      p.onGeloescht(s.name, ergebnis.ablageHinweis);
    }, "Das Layout konnte nicht gelöscht werden.");
  }

  return (
    <>
      <div className="lay__varianten" role="group" aria-label="Layout-Varianten">
        <Knopf art="leise" deaktiviert={p.gesperrt} onClick={() => oeffnen("kopie")}>Kopie anlegen …</Knopf>
        <Knopf art="leise" deaktiviert={p.gesperrt} onClick={() => oeffnen("name")}>Umbenennen …</Knopf>
        <Knopf art="leise" deaktiviert={p.gesperrt || s.vorgabe || archiviert} onClick={vorgabe}>
          Als Vorgabe</Knopf>
        <Knopf art="leise" deaktiviert={p.gesperrt || s.vorgabe} onClick={archiv}>
          {archiviert ? "Wiederherstellen" : "Archivieren"}</Knopf>
        <Knopf art="leise" deaktiviert={p.gesperrt || s.vorgabe || s.verwendung > 0} onClick={loeschen}>
          Löschen …</Knopf>
      </div>
      {dialog !== null && (
        <Modal titel={dialog.art === "kopie" ? "Layout kopieren" : "Layout umbenennen"}
          onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={formular.laeuft}>
          <Formular beschriftung="oben" {...formular.formularProps}>
            {dialog.art === "kopie" && (
              <p className="lay__hinweis">Die Kopie übernimmt Entwurf und Grafiken von „{s.name}“ und beginnt als
                Entwurf — freigegeben wird sie wie jedes Layout.</p>
            )}
            <Feld id="lay-variante-name" label="Name" bearbeitbar wert={dialog.name}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
              {...(formular.feldFehler.name === undefined ? {} : { fehler: formular.feldFehler.name })} />
          </Formular>
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          <ModalFuss speichernText={dialog.art === "kopie" ? "Anlegen" : "Speichern"}
            speichernGesperrt={formular.laeuft || dialog.name.trim() === ""}
            abbrechenGesperrt={formular.laeuft}
            onSpeichern={() => { void namensWeg(dialog); }} onAbbrechen={() => { void abbrechen(); }} />
        </Modal>
      )}
    </>
  );
}
